import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

export default function PaymentSuccess() {
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCleared, setHasCleared] = useState(false);

  useEffect(() => {
    if (!hasCleared) {
      clearCart(true);
      setHasCleared(true);
    }
  }, [clearCart, hasCleared]);

  useEffect(() => {
    const fetchLatestOrder = async (retries = 3) => {
      try {
        const res = await fetch('/api/orders/my-orders', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const orders = await res.json();
        
        if (orders && orders.length > 0) {
          const latestOrder = orders[0];
          console.log('Latest order:', latestOrder);
          
          // Extract vendor ID – handle both object and string formats
          let vendorId = null;
          if (latestOrder.hubId) {
            vendorId = latestOrder.hubId._id || latestOrder.hubId;
          } else if (latestOrder.producer) {
            vendorId = latestOrder.producer._id || latestOrder.producer;
          } else if (latestOrder.products && latestOrder.products.length > 0) {
            vendorId = latestOrder.products[0]?.producerId;
          }
          
          console.log('Extracted vendor ID:', vendorId);
          setOrder(latestOrder);
          
          if (vendorId) {
            const vendorRes = await fetch(`/api/users/${vendorId}`);
            const vendorData = await vendorRes.json();
            console.log('Vendor data:', vendorData);
            setVendor(vendorData);
          } else {
            console.log('No vendor ID found');
          }
        } else if (retries > 0) {
          console.log(`No orders yet, retrying... (${retries} left)`);
          setTimeout(() => fetchLatestOrder(retries - 1), 2000);
          return;
        }
      } catch (err) {
        console.error('Failed to fetch order:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLatestOrder();
  }, []);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-2">Order Confirmed!</h1>
          <p className="text-gray-600 mb-6">Loading your order details...</p>
        </div>
      </div>
    );
  }

  let vendorName = 'Vendor';
  if (vendor) {
    vendorName = vendor.farmName || vendor.name;
  } else if (order?.hubId) {
    vendorName = order.hubId.name || 'Hub';
  } else if (order?.producer) {
    vendorName = order.producer.name || 'Producer';
  } else if (order?.products?.[0]?.producerId) {
    vendorName = 'Farm';
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-2">Order Confirmed!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for your purchase from <strong>{vendorName}</strong>.
          </p>
        </div>
        
        {order && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="font-semibold text-lg mb-2">Order #{order._id.slice(-8).toUpperCase()}</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p>Placed on: {new Date(order.createdAt).toLocaleString()}</p>
              <p>Total: <strong className="text-green-600">${order.totalAmount?.toFixed(2)}</strong></p>
              <p>Status: <span className="capitalize">{order.status}</span></p>
              <div className="mt-2 pt-2 border-t">
                <p className="font-medium">Items:</p>
                <ul className="list-disc list-inside mt-1">
                  {order.products?.map((item, idx) => (
                    <li key={idx}>{item.name} x {item.quantity} — ${(item.price * item.quantity).toFixed(2)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">What happens next?</h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p>📦 <strong>{vendorName}</strong> will prepare your order for delivery.</p>
            <p>🚚 Once packed, the order will be released to our pickup network.</p>
            <p>📍 You can track your order status from your <Link to="/orders" className="underline">Orders page</Link>.</p>
            <p>📧 You will receive email updates when your order status changes.</p>
          </div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
          <h3 className="font-semibold text-yellow-800 mb-2">Delivery Information</h3>
          <div className="text-sm text-yellow-700">
            <p>Delivery is typically completed within 1-3 business days after order confirmation.</p>
            <p className="mt-1">Delivery address: {order?.deliveryAddress?.address}, {order?.deliveryAddress?.suburb}</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/orders" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-center">
            View My Orders
          </Link>
          <Link to="/products" className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 text-center">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
